import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import LongevityBot from './LongevityBot';
import {
  subscribeToPushNotifications,
  checkPushSubscriptionStatus
} from '../lib/pushNotifications';

const SESSION_STORAGE_KEY = 'notificationCardShown';

export default function NotificationCard() {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkIfShouldDisplay();
  }, []);

  const checkIfShouldDisplay = async () => {
    const hasBeenShown = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (hasBeenShown === 'true') {
      setIsVisible(false);
      setChecking(false);
      return;
    }

    const status = await checkPushSubscriptionStatus();
    setIsSubscribed(status);
    setChecking(false);

    if (!status) {
      // Small delay before showing modal
      setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 10);
      }, 800);
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    }
  };

  const handleEnable = async () => {
    setLoading(true);
    try {
      const subscription = await subscribeToPushNotifications();
      if (subscription) {
        setIsSubscribed(true);
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
      alert('Failed to enable notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  if (checking || !isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${
          isAnimating ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-white rounded-t-3xl px-5 pt-6 pb-6 max-w-md mx-auto">
          {/* Mascot */}
          <div className="flex justify-center mb-4">
            <LongevityBot size={100} mode="notification" />
          </div>

          {/* Text Content */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              How about only{' '}
              <span className="text-cyan-500">important</span>
              {' '}health reminders?
            </h2>
            <p className="text-gray-500 text-sm">
              We get it. Too many notifications can be annoying. We'll be careful.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="w-12 h-12 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
            >
              <X size={20} className="text-gray-600" strokeWidth={2.5} />
            </button>
            <button
              onClick={handleEnable}
              disabled={loading}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white py-3.5 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Enabling...
                </span>
              ) : (
                <>
                  <Bell size={18} strokeWidth={2.5} />
                  Let's turn them on
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}